/**
 * Multiplayer Game State Store - Client-side alkagi state with event sourcing
 *
 * Manages local physics state using @cloudnest/kinetic-core-engine.
 * The server acts as a dumb relay -- all game logic runs here on the client.
 *
 * Event sourcing model:
 * - Host creates initial state and submits as "init" event
 * - Actions are validated + applied locally, then submitted as "action" events
 * - Remote events received via WS are applied through the local engine
 * - On reconnect, getEvents() replays all events from initial state
 * - Game-over is detected locally, then submitted as "game_over" event
 */

import type {
  KineticCoreAction,
  KineticCoreState,
  TransitionRecording,
} from "@cloudnest/kinetic-core-engine";
import {
  applyConcede,
  applyImpulse,
  initMatch,
  validateConcede,
  validateImpulse,
} from "@cloudnest/kinetic-core-engine";
import { useSyncExternalStore } from "react";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
  type GameEvent,
} from "../../shared/services/game-api";
import { INIT_CONFIG } from "./config/init-config";
import {
  type ResolvedInitConfig,
  resolveInitConfig,
} from "./config/resolve-init-config";
import { resolveAlkagiGameApiMode } from "./route-mode";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function getGameApi(): GameApi {
  const nextMode = resolveAlkagiGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("alkagi", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

function isGamePlatformMode(): boolean {
  return resolveAlkagiGameApiMode() === "game-platform";
}

function participantIdForCurrentSeat(
  gameState: KineticCoreState | null,
): string | null {
  if (!gameState || state.isObserver) return null;
  const participant = state.isHost
    ? gameState.participants[0]
    : gameState.participants[1];
  return participant?.id ?? null;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function replayEventsUntilSequence(
  roomId: string,
  minSequenceNumber: number,
): Promise<boolean> {
  for (let attempt = 0; attempt < 8; attempt++) {
    const response = await getGameApi().getGameEvents(roomId);
    const events = response.success ? (response.data ?? []) : [];
    const latestSequence = events.reduce(
      (latest, event) => Math.max(latest, event.sequenceNumber),
      0,
    );

    if (latestSequence >= minSequenceNumber) {
      const { gameState, lastSeq } = replayAlkagiEvents(events);
      state = {
        ...state,
        gameState,
        myParticipantId: participantIdForCurrentSeat(gameState),
        lastAppliedSeq: lastSeq,
        lastError: null,
      };
      notify();
      return true;
    }

    await wait(250);
  }

  return false;
}

interface MultiplayerGameState {
  gameState: KineticCoreState | null;
  resolvedInitConfig: ResolvedInitConfig;
  roomId: string | null;
  myParticipantId: string | null;
  isHost: boolean;
  isObserver: boolean;
  lastAppliedSeq: number;
  lastError: string | null;
  selectedEntityId: string | null;
  isAnimating: boolean;
  animationRecording: TransitionRecording | null;
}

const resolvedInitConfig = resolveInitConfig(INIT_CONFIG);

const initialState: MultiplayerGameState = {
  gameState: null,
  resolvedInitConfig,
  roomId: null,
  myParticipantId: null,
  isHost: false,
  isObserver: false,
  lastAppliedSeq: 0,
  lastError: null,
  selectedEntityId: null,
  isAnimating: false,
  animationRecording: null,
};

let state: MultiplayerGameState = { ...initialState };

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

function applyKineticAction(
  gameState: KineticCoreState,
  action: KineticCoreAction,
  config: ResolvedInitConfig,
): KineticCoreState | null {
  if (action.type === "APPLY_IMPULSE") {
    const validation = validateImpulse(gameState, action, config.coreConfig);
    if (!validation.valid) return null;
    return applyImpulse(gameState, action, config.coreConfig);
  }
  if (action.type === "CONCEDE") {
    const validation = validateConcede(gameState, action);
    if (!validation.valid) return null;
    return applyConcede(gameState, action);
  }
  return null;
}

/**
 * Fold a persisted event log into the final alkagi state. Shared by
 * `replayEvents` (guest/reconnect sync) and the host-reconnect guard in
 * `initializeAsHost`, so both reconstruct the same state from the same log.
 * The caller decides `myParticipantId` (host = participant 1, guest = 2).
 */
function replayAlkagiEvents(events: readonly GameEvent[]): {
  gameState: KineticCoreState | null;
  lastSeq: number;
} {
  let currentState: KineticCoreState | null = null;
  let lastSeq = 0;

  for (const event of events) {
    if (event.eventType === "init") {
      currentState = event.payload.state as unknown as KineticCoreState;
      lastSeq = event.sequenceNumber;
    } else if (event.eventType === "action" && currentState) {
      const action = event.payload as unknown as KineticCoreAction;
      const newState = applyKineticAction(
        currentState,
        action,
        resolvedInitConfig,
      );
      if (newState) {
        currentState = newState;
      }
      lastSeq = event.sequenceNumber;
    } else if (event.eventType === "game_over") {
      lastSeq = event.sequenceNumber;
    }
  }

  return { gameState: currentState, lastSeq };
}

export const multiplayerGameActions = {
  /**
   * Initialize game as host. Fetches the persisted event log FIRST: if any
   * events exist the host is reconnecting (reload / remount), so REPLAY them
   * instead of submitting a new "init" — which would overwrite the in-progress
   * game and reset it to the start. Only the new-game path (empty log) inits.
   */
  initializeAsHost: async (roomId: string): Promise<void> => {
    state = {
      ...state,
      gameState: null,
      roomId,
      myParticipantId: null,
      isHost: true,
      isObserver: false,
      lastError: null,
      selectedEntityId: null,
      isAnimating: false,
      animationRecording: null,
    };
    notify();

    const eventsResponse = await getGameApi().getGameEvents(roomId);
    const existingEvents = eventsResponse.success
      ? (eventsResponse.data ?? [])
      : [];

    if (existingEvents.length > 0) {
      const { gameState, lastSeq } = replayAlkagiEvents(existingEvents);
      state = {
        ...state,
        gameState,
        // Host is always participant 1.
        myParticipantId: gameState ? gameState.participants[0].id : null,
        lastAppliedSeq: lastSeq,
        lastError: null,
      };
      notify();
      return;
    }

    // No events yet — brand-new game: create initial state and persist it.
    const gameState = initMatch({
      participant1Name: resolvedInitConfig.source.participants.playerOneName,
      participant2Name: resolvedInitConfig.source.participants.playerTwoName,
      config: resolvedInitConfig.coreConfig,
      initialEntities: resolvedInitConfig.initialEntities,
    });

    // Host is always participant 1
    const myParticipantId = gameState.participants[0].id;

    state = {
      ...state,
      gameState,
      myParticipantId,
      lastAppliedSeq: 0,
      lastError: null,
      selectedEntityId: null,
      isAnimating: false,
      animationRecording: null,
    };
    notify();

    const initPayload = isGamePlatformMode()
      ? {}
      : { state: gameState as unknown as Record<string, unknown> };

    const response = await getGameApi().submitEvent(
      roomId,
      "init",
      initPayload,
    );

    if (response.success && response.data) {
      if (isGamePlatformMode()) {
        const synced = await replayEventsUntilSequence(
          roomId,
          response.data.sequenceNumber,
        );
        if (!synced) {
          state = {
            ...state,
            lastError: "서버 초기 상태 동기화가 지연되고 있습니다",
          };
          notify();
        }
        return;
      }

      const replay = replayAlkagiEvents([response.data]);
      state = {
        ...state,
        gameState: replay.gameState ?? state.gameState,
        myParticipantId:
          replay.gameState?.participants[0].id ?? state.myParticipantId,
        lastAppliedSeq: replay.lastSeq,
      };
      notify();
    }
  },

  initializeAsGuest: (roomId: string): void => {
    state = {
      ...state,
      gameState: null,
      roomId,
      myParticipantId: null,
      isHost: false,
      isObserver: false,
      lastAppliedSeq: 0,
      lastError: null,
      selectedEntityId: null,
      isAnimating: false,
      animationRecording: null,
    };
    notify();
  },

  initializeAsObserver: async (roomId: string): Promise<void> => {
    state = {
      ...state,
      gameState: null,
      roomId,
      myParticipantId: null,
      isHost: false,
      isObserver: true,
      lastAppliedSeq: 0,
      lastError: null,
      selectedEntityId: null,
      isAnimating: false,
      animationRecording: null,
    };
    notify();

    await multiplayerGameActions.replayEvents(roomId);
  },

  replayEvents: async (roomId: string): Promise<void> => {
    const response = await getGameApi().getGameEvents(roomId);

    if (!response.success || !response.data) {
      state = {
        ...state,
        lastError:
          "\uC774\uBCA4\uD2B8\uB97C \uBD88\uB7EC\uC62C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
      };
      notify();
      return;
    }

    const { gameState, lastSeq } = replayAlkagiEvents(response.data);

    state = {
      ...state,
      gameState,
      myParticipantId: participantIdForCurrentSeat(gameState),
      lastAppliedSeq: lastSeq,
      lastError: null,
    };
    notify();
  },

  applyRemoteEvent: (
    eventType: string,
    payload: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;

    if (eventType === "init") {
      const initState = payload.state as unknown as KineticCoreState;

      state = {
        ...state,
        gameState: initState,
        myParticipantId: participantIdForCurrentSeat(initState),
        lastAppliedSeq: seq,
      };
      notify();
      return;
    }

    if (eventType === "action" && state.gameState) {
      const action = payload as unknown as KineticCoreAction;
      const newState = applyKineticAction(
        state.gameState,
        action,
        resolvedInitConfig,
      );
      if (newState) {
        state = {
          ...state,
          gameState: newState,
          lastAppliedSeq: seq,
          isAnimating:
            action.type === "APPLY_IMPULSE" && newState.lastRecording !== null,
          animationRecording:
            action.type === "APPLY_IMPULSE"
              ? newState.lastRecording
              : state.animationRecording,
        };
        notify();
      }
      return;
    }

    if (eventType === "game_over") {
      state = { ...state, lastAppliedSeq: seq };
      notify();
    }
  },

  submitAction: async (action: KineticCoreAction): Promise<boolean> => {
    const { gameState, roomId } = state;
    if (!gameState || !roomId || state.isObserver) return false;

    const newState = applyKineticAction(gameState, action, resolvedInitConfig);
    if (!newState) {
      state = {
        ...state,
        lastError:
          "\uC720\uD6A8\uD558\uC9C0 \uC54A\uC740 \uD589\uB3D9\uC785\uB2C8\uB2E4",
      };
      notify();
      return false;
    }

    const previousState = gameState;
    state = {
      ...state,
      gameState: newState,
      lastError: null,
      selectedEntityId: null,
      isAnimating:
        action.type === "APPLY_IMPULSE" && newState.lastRecording !== null,
      animationRecording:
        action.type === "APPLY_IMPULSE" ? newState.lastRecording : null,
    };
    notify();

    const response = await getGameApi().submitEvent(
      roomId,
      "action",
      action as unknown as Record<string, unknown>,
    );

    if (!response.success) {
      state = {
        ...state,
        gameState: previousState,
        lastError: response.error ?? "\uD589\uB3D9 \uC2E4\uD328",
        isAnimating: false,
        animationRecording: null,
      };
      notify();
      return false;
    }

    if (response.data) {
      state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
      notify();
    }

    // Check for game-over
    if (
      !isGamePlatformMode() &&
      newState.phase === "ENDED" &&
      action.type !== "CONCEDE"
    ) {
      const isDraw = newState.isDraw;
      await getGameApi().submitEvent(roomId, "game_over", {
        winnerId: isDraw ? null : newState.winner,
        isDraw,
        reason: newState.outcomeReason,
      });
    }

    return true;
  },

  submitConcede: async (): Promise<void> => {
    const { roomId, myParticipantId } = state;
    if (!roomId || !myParticipantId || state.isObserver) return;

    const action: KineticCoreAction = {
      type: "CONCEDE",
      participantId: myParticipantId,
    };

    await getGameApi().submitEvent(
      roomId,
      "action",
      action as unknown as Record<string, unknown>,
    );

    const winner = state.gameState?.participants.find(
      (p) => p.id !== myParticipantId,
    )?.id;

    if (!isGamePlatformMode()) {
      await getGameApi().submitEvent(roomId, "game_over", {
        winnerId: winner ?? null,
        isDraw: winner === undefined,
        reason: "CONCEDE",
      });
    }
  },

  selectEntity: (entityId: string | null): void => {
    state = { ...state, selectedEntityId: entityId, lastError: null };
    notify();
  },

  finishAnimation: (): void => {
    state = {
      ...state,
      isAnimating: false,
      animationRecording: null,
    };
    notify();
  },

  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  reset: (): void => {
    state = {
      ...initialState,
      resolvedInitConfig: state.resolvedInitConfig,
    };
    notify();
  },
};

export const multiplayerGameStore = {
  getState: (): MultiplayerGameState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useMultiplayerGame(): MultiplayerGameState {
  return useSyncExternalStore(
    multiplayerGameStore.subscribe,
    multiplayerGameStore.getState,
  );
}

export function useMultiplayerGameActions(): typeof multiplayerGameActions {
  return multiplayerGameActions;
}
